'use client';

import { c as _c } from "react/compiler-runtime";
import { Badge, cn, Collapsible, CollapsibleContent, CollapsibleTrigger } from '@newfront-insurance/core-ui/v2';
import type { LucideIcon } from 'lucide-react';
import { BrainIcon, ChevronDownIcon, DotIcon } from 'lucide-react';
import type { ComponentProps, ReactNode } from 'react';
import { createContext, memo, useContext, useMemo } from 'react';
import { useAutoCloseCollapsible } from './use-auto-close-collapsible';

// ---------------------------------------------------------------------------
// Context
// ---------------------------------------------------------------------------

interface ChainOfThoughtContextValue {
  isStreaming: boolean;
}
const ChainOfThoughtContext = createContext<ChainOfThoughtContextValue | null>(null);
const useChainOfThought = () => {
  const context = useContext(ChainOfThoughtContext);
  if (!context) {
    throw new Error("ChainOfThought components must be used within ChainOfThought");
  }
  return context;
};

// ---------------------------------------------------------------------------
// ChainOfThought (root) — single Collapsible wrapping trigger + content
// ---------------------------------------------------------------------------

export type ChainOfThoughtProps = ComponentProps<typeof Collapsible> & {
  isStreaming?: boolean;
  open?: boolean;
  defaultOpen?: boolean;
  onOpenChange?: (open: boolean) => void;
};
export function ChainOfThought(t0) {
  const $ = _c(26);
  let children;
  let className;
  let onOpenChange;
  let open;
  let props;
  let t1;
  let t2;
  if ($[0] !== t0) {
    ({
      className,
      isStreaming: t1,
      open,
      defaultOpen: t2,
      onOpenChange,
      children,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = children;
    $[2] = className;
    $[3] = onOpenChange;
    $[4] = open;
    $[5] = props;
    $[6] = t1;
    $[7] = t2;
  } else {
    children = $[1];
    className = $[2];
    onOpenChange = $[3];
    open = $[4];
    props = $[5];
    t1 = $[6];
    t2 = $[7];
  }
  const isStreaming = t1 === undefined ? false : t1;
  const defaultOpen = t2 === undefined ? false : t2;
  let t3;
  if ($[8] !== defaultOpen || $[9] !== isStreaming || $[10] !== onOpenChange || $[11] !== open) {
    t3 = {
      isStreaming,
      open,
      defaultOpen,
      onOpenChange
    };
    $[8] = defaultOpen;
    $[9] = isStreaming;
    $[10] = onOpenChange;
    $[11] = open;
    $[12] = t3;
  } else {
    t3 = $[12];
  }
  const {
    isOpen,
    handleOpenChange
  } = useAutoCloseCollapsible(t3);
  let t4;
  let t5;
  if ($[13] !== isStreaming) {
    t5 = {
      isStreaming
    };
    $[13] = isStreaming;
    $[14] = t5;
  } else {
    t5 = $[14];
  }
  t4 = t5;
  const contextValue = t4;
  let t6;
  if ($[15] !== className) {
    t6 = cn("not-prose mb-4 w-full", className);
    $[15] = className;
    $[16] = t6;
  } else {
    t6 = $[16];
  }
  let t7;
  if ($[17] !== children || $[18] !== handleOpenChange || $[19] !== isOpen || $[20] !== props || $[21] !== t6) {
    t7 = <Collapsible className={t6} onOpenChange={handleOpenChange} open={isOpen} {...props}>{children}</Collapsible>;
    $[17] = children;
    $[18] = handleOpenChange;
    $[19] = isOpen;
    $[20] = props;
    $[21] = t6;
    $[22] = t7;
  } else {
    t7 = $[22];
  }
  let t8;
  if ($[23] !== contextValue || $[24] !== t7) {
    t8 = <ChainOfThoughtContext.Provider value={contextValue}>{t7}</ChainOfThoughtContext.Provider>;
    $[23] = contextValue;
    $[24] = t7;
    $[25] = t8;
  } else {
    t8 = $[25];
  }
  return t8;
}

// ---------------------------------------------------------------------------
// ChainOfThoughtHeader (CollapsibleTrigger)
// ---------------------------------------------------------------------------

export type ChainOfThoughtHeaderProps = ComponentProps<typeof CollapsibleTrigger>;
export const ChainOfThoughtHeader = memo(t0 => {
  const $ = _c(15);
  let children;
  let className;
  let props;
  if ($[0] !== t0) {
    ({
      className,
      children,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = children;
    $[2] = className;
    $[3] = props;
  } else {
    children = $[1];
    className = $[2];
    props = $[3];
  }
  const {
    isStreaming
  } = useChainOfThought();
  const t1 = isStreaming && "animate-pulse";
  let t2;
  if ($[4] !== className || $[5] !== t1) {
    t2 = cn("text-muted-foreground flex w-full items-center gap-2 text-sm transition-colors", t1, className);
    $[4] = className;
    $[5] = t1;
    $[6] = t2;
  } else {
    t2 = $[6];
  }
  let t3;
  if ($[7] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = <BrainIcon className="size-4" />;
    $[7] = t3;
  } else {
    t3 = $[7];
  }
  const t4 = children ?? "Chain of Thought";
  let t5;
  if ($[8] !== t4) {
    t5 = <span className="flex-1 text-left">{t4}</span>;
    $[8] = t4;
    $[9] = t5;
  } else {
    t5 = $[9];
  }
  let t6;
  if ($[10] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = <ChevronDownIcon className="size-4 transition-transform [[data-state=open]>&]:rotate-180" />;
    $[10] = t6;
  } else {
    t6 = $[10];
  }
  let t7;
  if ($[11] !== props || $[12] !== t2 || $[13] !== t5) {
    t7 = <CollapsibleTrigger className={t2} {...props}>{t3}{t5}{t6}</CollapsibleTrigger>;
    $[11] = props;
    $[12] = t2;
    $[13] = t5;
    $[14] = t7;
  } else {
    t7 = $[14];
  }
  return t7;
});

// ---------------------------------------------------------------------------
// ChainOfThoughtStep
// ---------------------------------------------------------------------------

export type ChainOfThoughtStepProps = ComponentProps<'div'> & {
  icon?: LucideIcon;
  label: ReactNode;
  description?: ReactNode;
  status?: 'complete' | 'active' | 'pending';
  isLast?: boolean;
};
const stepStatusStyles = {
  active: 'text-muted-foreground',
  complete: 'text-muted-foreground',
  pending: 'text-muted-foreground/50'
};
export const ChainOfThoughtStep = memo(t0 => {
  const $ = _c(42);
  let children;
  let className;
  let description;
  let label;
  let props;
  let t1;
  let t2;
  let t3;
  if ($[0] !== t0) {
    ({
      className,
      icon: t1,
      label,
      description,
      status: t2,
      isLast: t3,
      children,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = children;
    $[2] = className;
    $[3] = description;
    $[4] = label;
    $[5] = props;
    $[6] = t1;
    $[7] = t2;
    $[8] = t3;
  } else {
    children = $[1];
    className = $[2];
    description = $[3];
    label = $[4];
    props = $[5];
    t1 = $[6];
    t2 = $[7];
    t3 = $[8];
  }
  const Icon = t1 === undefined ? DotIcon : t1;
  const status = t2 === undefined ? "complete" : t2;
  const isLast = t3 === undefined ? false : t3;
  const t4 = stepStatusStyles[status];
  let t5;
  if ($[9] !== className || $[10] !== t4) {
    t5 = cn("flex gap-2 text-sm", t4, "fade-in-0 slide-in-from-top-2 animate-in", className);
    $[9] = className;
    $[10] = t4;
    $[11] = t5;
  } else {
    t5 = $[11];
  }
  const t6 = status === "active" && "animate-pulse";
  let t7;
  if ($[12] !== t6) {
    t7 = cn("size-4", t6);
    $[12] = t6;
    $[13] = t7;
  } else {
    t7 = $[13];
  }
  let t8;
  if ($[14] !== Icon || $[15] !== t7) {
    t8 = <Icon className={t7} />;
    $[14] = Icon;
    $[15] = t7;
    $[16] = t8;
  } else {
    t8 = $[16];
  }
  let t9;
  if ($[17] !== isLast || $[18] !== status) {
    t9 = !isLast && status !== "active" && <div className="bg-border absolute top-5 bottom-0 left-1/2 -mx-px w-px" />;
    $[17] = isLast;
    $[18] = status;
    $[19] = t9;
  } else {
    t9 = $[19];
  }
  let t10;
  if ($[20] !== t8 || $[21] !== t9) {
    t10 = <div className="relative mt-0.5 w-4 shrink-0">{t8}{t9}</div>;
    $[20] = t8;
    $[21] = t9;
    $[22] = t10;
  } else {
    t10 = $[22];
  }
  let t11;
  if ($[23] !== label) {
    t11 = <div>{label}</div>;
    $[23] = label;
    $[24] = t11;
  } else {
    t11 = $[24];
  }
  let t12;
  if ($[25] !== description) {
    t12 = description && <div className="text-muted-foreground">{description}</div>;
    $[25] = description;
    $[26] = t12;
  } else {
    t12 = $[26];
  }
  let t13;
  if ($[27] !== children || $[28] !== t11 || $[29] !== t12) {
    t13 = <div className="min-w-0 flex-1 space-y-2">{t11}{t12}{children}</div>;
    $[27] = children;
    $[28] = t11;
    $[29] = t12;
    $[30] = t13;
  } else {
    t13 = $[30];
  }
  let t14;
  if ($[31] !== props || $[32] !== t10 || $[33] !== t13 || $[34] !== t5) {
    t14 = <div className={t5} {...props}>{t10}{t13}</div>;
    $[31] = props;
    $[32] = t10;
    $[33] = t13;
    $[34] = t5;
    $[35] = t14;
  } else {
    t14 = $[35];
  }
  let t15;
  if ($[36] !== isLast || $[37] !== status) {
    t15 = !isLast && status !== "active" && <div className="flex gap-2"><div className="relative w-4 shrink-0"><div className="bg-border absolute top-0 bottom-0 left-1/2 -mx-px w-px" /><div className="h-3" /></div></div>;
    $[36] = isLast;
    $[37] = status;
    $[38] = t15;
  } else {
    t15 = $[38];
  }
  let t16;
  if ($[39] !== t14 || $[40] !== t15) {
    t16 = <>{t14}{t15}</>;
    $[39] = t14;
    $[40] = t15;
    $[41] = t16;
  } else {
    t16 = $[41];
  }
  return t16;
});

// ---------------------------------------------------------------------------
// ChainOfThoughtContent (CollapsibleContent)
// ---------------------------------------------------------------------------

export type ChainOfThoughtContentProps = ComponentProps<typeof CollapsibleContent>;
export const ChainOfThoughtContent = memo(t0 => {
  const $ = _c(11);
  let children;
  let className;
  let props;
  if ($[0] !== t0) {
    ({
      className,
      children,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = children;
    $[2] = className;
    $[3] = props;
  } else {
    children = $[1];
    className = $[2];
    props = $[3];
  }
  let t1;
  if ($[4] !== className) {
    t1 = cn("mt-1", "data-[state=closed]:fade-out-0 data-[state=closed]:slide-out-to-top-2 data-[state=open]:slide-in-from-top-2", "text-popover-foreground data-[state=closed]:animate-out data-[state=open]:animate-in outline-none", className);
    $[4] = className;
    $[5] = t1;
  } else {
    t1 = $[5];
  }
  let t2;
  if ($[6] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = <div className="flex gap-2"><div className="relative w-4 shrink-0"><div className="bg-border absolute top-0 bottom-0 left-1/2 -mx-px w-px" /><div className="h-3" /></div></div>;
    $[6] = t2;
  } else {
    t2 = $[6];
  }
  let t3;
  if ($[7] !== children || $[8] !== props || $[9] !== t1) {
    t3 = <CollapsibleContent className={t1} {...props}>{t2}{children}</CollapsibleContent>;
    $[7] = children;
    $[8] = props;
    $[9] = t1;
    $[10] = t3;
  } else {
    t3 = $[10];
  }
  return t3;
});

// ---------------------------------------------------------------------------
// ChainOfThoughtSearchResults / SearchResult / Image
// ---------------------------------------------------------------------------

export type ChainOfThoughtSearchResultsProps = ComponentProps<'div'>;
export const ChainOfThoughtSearchResults = memo(t0 => {
  const $ = _c(8);
  let className;
  let props;
  if ($[0] !== t0) {
    ({
      className,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = className;
    $[2] = props;
  } else {
    className = $[1];
    props = $[2];
  }
  let t1;
  if ($[3] !== className) {
    t1 = cn("flex flex-wrap items-center gap-2", className);
    $[3] = className;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  let t2;
  if ($[5] !== props || $[6] !== t1) {
    t2 = <div className={t1} {...props} />;
    $[5] = props;
    $[6] = t1;
    $[7] = t2;
  } else {
    t2 = $[7];
  }
  return t2;
});
export type ChainOfThoughtSearchResultProps = ComponentProps<typeof Badge>;
export const ChainOfThoughtSearchResult = memo(t0 => {
  const $ = _c(10);
  let children;
  let className;
  let props;
  if ($[0] !== t0) {
    ({
      className,
      children,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = children;
    $[2] = className;
    $[3] = props;
  } else {
    children = $[1];
    className = $[2];
    props = $[3];
  }
  let t1;
  if ($[4] !== className) {
    t1 = cn("gap-1 px-2 py-0.5 text-xs font-normal", className);
    $[4] = className;
    $[5] = t1;
  } else {
    t1 = $[5];
  }
  let t2;
  if ($[6] !== children || $[7] !== props || $[8] !== t1) {
    t2 = <Badge className={t1} variant="secondary" {...props}>{children}</Badge>;
    $[6] = children;
    $[7] = props;
    $[8] = t1;
    $[9] = t2;
  } else {
    t2 = $[9];
  }
  return t2;
});
export type ChainOfThoughtImageProps = ComponentProps<'div'> & {
  caption?: string;
};
export const ChainOfThoughtImage = memo(t0 => {
  const $ = _c(16);
  let caption;
  let children;
  let className;
  let props;
  if ($[0] !== t0) {
    ({
      className,
      children,
      caption,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = caption;
    $[2] = children;
    $[3] = className;
    $[4] = props;
  } else {
    caption = $[1];
    children = $[2];
    className = $[3];
    props = $[4];
  }
  let t1;
  if ($[5] !== className) {
    t1 = cn("mt-2 space-y-2", className);
    $[5] = className;
    $[6] = t1;
  } else {
    t1 = $[6];
  }
  let t2;
  if ($[7] !== children) {
    t2 = <div className="bg-muted relative flex max-h-[22rem] items-center justify-center overflow-hidden rounded-lg p-3">{children}</div>;
    $[7] = children;
    $[8] = t2;
  } else {
    t2 = $[8];
  }
  let t3;
  if ($[9] !== caption) {
    t3 = caption && <p className="text-muted-foreground text-xs">{caption}</p>;
    $[9] = caption;
    $[10] = t3;
  } else {
    t3 = $[10];
  }
  let t4;
  if ($[11] !== props || $[12] !== t1 || $[13] !== t2 || $[14] !== t3) {
    t4 = <div className={t1} {...props}>{t2}{t3}</div>;
    $[11] = props;
    $[12] = t1;
    $[13] = t2;
    $[14] = t3;
    $[15] = t4;
  } else {
    t4 = $[15];
  }
  return t4;
});

// ---------------------------------------------------------------------------
// Display names
// ---------------------------------------------------------------------------

ChainOfThought.displayName = 'ChainOfThought';
ChainOfThoughtHeader.displayName = 'ChainOfThoughtHeader';
ChainOfThoughtStep.displayName = 'ChainOfThoughtStep';
ChainOfThoughtSearchResults.displayName = 'ChainOfThoughtSearchResults';
ChainOfThoughtSearchResult.displayName = 'ChainOfThoughtSearchResult';
ChainOfThoughtContent.displayName = 'ChainOfThoughtContent';
ChainOfThoughtImage.displayName = 'ChainOfThoughtImage';